"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Star, ThumbsUp, MessageCircle, BadgeCheck, Pencil, Trash2, EyeOff, Eye, ChevronLeft, ChevronRight, X, ShieldCheck } from "lucide-react";
import { fetchWrapper } from "@/helpers";
import { userService } from "@/services/user.service";
import { usePWA } from "@/utlis/usePWA";
import { useIsMobile } from "@/utlis/useIsMobile";
import Avatar from "@/components/common/Avatar";
import ReviewForm from "./ReviewForm";
import { ConfirmDrawer, EditTextDrawer } from "./MobileDialogs";

const STAR_COLORS = { 5: "#22c55e", 4: "#84cc16", 3: "#f59e0b", 2: "#f97316", 1: "#ef4444" };

function StarRow({ rating, size = 16 }) {
	return (
		<div className='tf-review-stars' role='img' aria-label={`${rating} din 5 stele`}>
			{[1, 2, 3, 4, 5].map((n) => (
				<Star key={n} size={size} fill={n <= rating ? "#ffb800" : "none"} color={n <= rating ? "#ffb800" : "#c7c7c7"} />
			))}
		</div>
	);
}

function formatDate(dateStr) {
	return new Date(dateStr).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
}

function CommentItem({ comment, reviewID, depth, isAdmin, replyingTo, setReplyingTo, onSubmitReply, onEditComment, onDeleteComment, onHideComment }) {
	const [replyText, setReplyText] = useState("");
	const [replyAsAdmin, setReplyAsAdmin] = useState(false);
	const isReplying = replyingTo === comment.id;
	const canManage = comment.isMine || isAdmin;

	if (comment.hidden && !isAdmin) return null;

	return (
		<div className={`tf-review-comment ${comment.deletedByUser ? "is-deleted" : ""} ${comment.hidden ? "is-hidden" : ""}`} style={{ marginLeft: depth * 24 }}>
			<div className='tf-review-comment-head'>
				{comment.isAdminReply ? (
					<img src='/images/logo/emagyl.png' alt='' className='tf-review-admin-logo' />
				) : (
					<Avatar url={comment.userAvatar} name={comment.userName} size={26} />
				)}
				<span className='tf-review-comment-author'>{comment.userName}</span>
				{comment.isAdminReply && <span className='tf-review-admin-badge'>Răspuns oficial</span>}
				<span className='tf-review-comment-date'>{formatDate(comment.createdAt)}</span>
				{isAdmin && comment.deletedByUser && <span className='tf-review-flag-badge'>șters de utilizator</span>}
				{isAdmin && comment.hidden && <span className='tf-review-flag-badge'>ascuns</span>}

				<div className='tf-review-comment-actions-admin'>
					{comment.isMine && !comment.deletedByUser && (
						<button type='button' onClick={() => onEditComment({ ...comment, reviewID })} aria-label='Editează comentariul'>
							<Pencil size={13} />
						</button>
					)}
					{isAdmin && (
						<button type='button' onClick={() => onHideComment({ ...comment, reviewID })} aria-label={comment.hidden ? "Arată comentariul" : "Ascunde comentariul"}>
							{comment.hidden ? <Eye size={13} /> : <EyeOff size={13} />}
						</button>
					)}
					{canManage && (
						<button type='button' onClick={() => onDeleteComment({ ...comment, reviewID })} aria-label='Șterge comentariul'>
							<Trash2 size={13} />
						</button>
					)}
				</div>
			</div>

			<p className='tf-review-comment-text'>{comment.deletedByUser ? "[comentariu șters de utilizator]" : comment.text}</p>

			{!comment.deletedByUser && (
				<button type='button' className='tf-review-comment-reply-btn' onClick={() => setReplyingTo(isReplying ? null : comment.id)}>
					Răspunde
				</button>
			)}

			{isReplying && (
				<div className='tf-review-comment-reply-box'>
					<textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder='Scrie un răspuns...' />
					{isAdmin && (
						<label className='tf-review-admin-checkbox'>
							<input type='checkbox' checked={replyAsAdmin} onChange={(e) => setReplyAsAdmin(e.target.checked)} />
							Comentează ca eMagyl Support
						</label>
					)}
					<div className='tf-review-comment-reply-actions'>
						<button type='button' onClick={() => setReplyingTo(null)}>
							Anulează
						</button>
						<button
							type='button'
							className='tf-review-comment-reply-send'
							onClick={() => {
								if (!replyText.trim()) return;
								onSubmitReply(reviewID, comment.id, replyText.trim(), replyAsAdmin);
								setReplyText("");
								setReplyingTo(null);
							}}>
							Trimite
						</button>
					</div>
				</div>
			)}

			{comment.replies?.map((reply) => (
				<CommentItem
					key={reply.id}
					comment={reply}
					reviewID={reviewID}
					depth={depth + 1}
					isAdmin={isAdmin}
					replyingTo={replyingTo}
					setReplyingTo={setReplyingTo}
					onSubmitReply={onSubmitReply}
					onEditComment={onEditComment}
					onDeleteComment={onDeleteComment}
					onHideComment={onHideComment}
				/>
			))}
		</div>
	);
}

function Lightbox({ images, index, onClose, onNavigate }) {
	if (index == null) return null;
	return (
		<div className='tf-review-lightbox' onClick={onClose}>
			<button type='button' className='tf-review-lightbox-close' onClick={onClose} aria-label='Închide'>
				<X size={22} />
			</button>
			{images.length > 1 && (
				<button
					type='button'
					className='tf-review-lightbox-nav tf-review-lightbox-prev'
					onClick={(e) => {
						e.stopPropagation();
						onNavigate((index - 1 + images.length) % images.length);
					}}
					aria-label='Anterior'>
					<ChevronLeft size={28} />
				</button>
			)}
			<img src={images[index]} alt='' onClick={(e) => e.stopPropagation()} />
			{images.length > 1 && (
				<button
					type='button'
					className='tf-review-lightbox-nav tf-review-lightbox-next'
					onClick={(e) => {
						e.stopPropagation();
						onNavigate((index + 1) % images.length);
					}}
					aria-label='Următor'>
					<ChevronRight size={28} />
				</button>
			)}
		</div>
	);
}

export default function ReviewsSection({ product }) {
	const router = useRouter();
	const isPWA = usePWA();
	const isMobile = useIsMobile();
	const isCompact = isPWA || isMobile;

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [sort, setSort] = useState("newest");
	const [starFilter, setStarFilter] = useState(0);
	const [showForm, setShowForm] = useState(false);
	const [formMode, setFormMode] = useState("create");
	const [editingReview, setEditingReview] = useState(null);
	const [openComments, setOpenComments] = useState({});
	const [replyingTo, setReplyingTo] = useState({});
	const [newComment, setNewComment] = useState({});
	const [newCommentAsAdmin, setNewCommentAsAdmin] = useState({});
	const [lightbox, setLightbox] = useState(null);
	const [confirmDialog, setConfirmDialog] = useState(null);
	const [editDialog, setEditDialog] = useState(null);
	const dialogResolverRef = useRef(null);

	const fetchReviews = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams({ page, sort });
			if (starFilter) params.set("star", starFilter);
			const res = await fetchWrapper.get(`/api/reviews/${product.id}?${params.toString()}`);
			setData(res);
		} catch (err) {
			console.error("Eroare la încărcarea recenziilor:", err);
		} finally {
			setLoading(false);
		}
	}, [product.id, page, sort, starFilter]);

	useEffect(() => {
		fetchReviews();
	}, [fetchReviews]);

	const askConfirm = (title, text, confirmText = "Da, șterge") => {
		if (!isCompact) {
			return Swal.fire({
				title,
				text,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: confirmText,
				cancelButtonText: "Anulează",
				confirmButtonColor: "#e53e3e",
				cancelButtonColor: "#718096",
				reverseButtons: true,
				focusCancel: true,
			}).then((r) => r.isConfirmed);
		}
		return new Promise((resolve) => {
			dialogResolverRef.current = resolve;
			setConfirmDialog({ title, text, confirmText });
		});
	};

	const askEditText = (title, initialValue) => {
		if (!isCompact) {
			return Swal.fire({
				title,
				input: "textarea",
				inputValue: initialValue,
				showCancelButton: true,
				confirmButtonText: "Salvează",
				cancelButtonText: "Anulează",
			}).then((r) => (r.isConfirmed ? r.value : null));
		}
		return new Promise((resolve) => {
			dialogResolverRef.current = resolve;
			setEditDialog({ title, initialValue });
		});
	};

	const ensureLoggedIn = () => {
		if (userService.userValue) return true;
		toast.info("Trebuie să fii conectat pentru a continua.");
		router.push("/autentificare");
		return false;
	};

	const handleAddReviewClick = () => {
		if (!ensureLoggedIn()) return;
		if (!data.hasPurchased) {
			toast.info("Poți lăsa un review doar dacă ai cumpărat acest produs.");
			return;
		}
		setFormMode("create");
		setEditingReview(null);
		setShowForm(true);
	};

	const handleLike = async (reviewID) => {
		if (!ensureLoggedIn()) return;
		setData((prev) => ({
			...prev,
			reviews: prev.reviews.map((r) =>
				r.id === reviewID ? { ...r, likedByMe: !r.likedByMe, likesCount: r.likesCount + (r.likedByMe ? -1 : 1) } : r
			),
		}));
		try {
			await fetchWrapper.post(`/api/reviews/${reviewID}/like`);
		} catch {
			fetchReviews();
		}
	};

	const handleSubmitComment = async (reviewID, parentID, text, asAdmin) => {
		if (!ensureLoggedIn()) return;
		try {
			await fetchWrapper.post(`/api/reviews/${reviewID}/comments`, { text, parentID, asAdmin });
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Comentariul nu a putut fi trimis.");
		}
	};

	const handleEditComment = async (comment) => {
		const newText = await askEditText("Editează comentariul", comment.text);
		if (!newText || !newText.trim()) return;
		try {
			await fetchWrapper.put(`/api/reviews/${comment.reviewID || comment.id}/comment/${comment.id}`, { text: newText.trim() });
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Comentariul nu a putut fi actualizat.");
		}
	};

	const handleDeleteComment = async (comment) => {
		const confirmed = await askConfirm("Ștergi acest comentariu?", "Această acțiune nu poate fi anulată.");
		if (!confirmed) return;
		try {
			if (comment.isMine && !data.isAdmin) {
				await fetchWrapper.delete(`/api/reviews/${comment.reviewID || comment.id}/comment/${comment.id}`);
			} else {
				await fetchWrapper.delete(`/api/admin/reviews/comments/${comment.id}`);
			}
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Comentariul nu a putut fi șters.");
		}
	};

	const handleHideComment = async (comment) => {
		try {
			await fetchWrapper.put(`/api/admin/reviews/comments/${comment.id}`, { hidden: !comment.hidden });
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Acțiunea a eșuat.");
		}
	};

	const handleEditReview = (review) => {
		setFormMode(data.isAdmin && !review.isMine ? "adminEdit" : "ownEdit");
		setEditingReview(review);
		setShowForm(true);
	};

	const handleDeleteReview = async (review) => {
		const confirmed = await askConfirm("Ștergi acest review?", "Comentariile și aprecierile asociate vor fi șterse automat.");
		if (!confirmed) return;
		try {
			if (review.isMine && !data.isAdmin) {
				await fetchWrapper.delete(`/api/reviews/${review.id}/own`);
			} else {
				await fetchWrapper.delete(`/api/admin/reviews/${review.id}`);
			}
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Review-ul nu a putut fi șters.");
		}
	};

	const handleHideReview = async (review) => {
		try {
			await fetchWrapper.put(`/api/admin/reviews/${review.id}`, { hidden: !review.hidden });
			fetchReviews();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Acțiunea a eșuat.");
		}
	};

	if (loading && !data) return <div className='tf-reviews-loading'>Se încarcă recenziile...</div>;
	if (!data) return null;

	const { summary, reviews, totalPages, canReview, isAdmin } = data;

	return (
		<div className='tf-reviews-section'>
			<div className='tf-reviews-summary'>
				<div className='tf-reviews-summary-score'>
					<div className='tf-reviews-summary-avg'>{summary.total ? summary.average : "-"}</div>
					<StarRow rating={Math.round(summary.average)} size={18} />
					<div className='tf-reviews-summary-count'>{summary.total} review-uri</div>
				</div>

				<div className='tf-reviews-summary-bars'>
					{[5, 4, 3, 2, 1].map((star) => {
						const count = summary.counts[star] || 0;
						const pct = summary.total ? Math.round((count / summary.total) * 100) : 0;
						return (
							<button
								type='button'
								key={star}
								className={`tf-reviews-bar-row ${starFilter === star ? "active" : ""}`}
								onClick={() => {
									setStarFilter(starFilter === star ? 0 : star);
									setPage(1);
								}}>
								<span className='tf-reviews-bar-label'>{star} stele</span>
								<span className='tf-reviews-bar-track'>
									<span className='tf-reviews-bar-fill' style={{ width: `${pct}%`, background: STAR_COLORS[star] }} />
								</span>
								<span className='tf-reviews-bar-count'>({count})</span>
							</button>
						);
					})}
				</div>

				<div className='tf-reviews-summary-stats'>
					<BadgeCheck size={22} color='#2b9d31' />
					<div>
						<div className='tf-reviews-summary-stats-value'>{summary.recommendPercent}%</div>
						<div className='tf-reviews-summary-stats-label'>recomandă produsul</div>
					</div>
				</div>

				<div className='tf-reviews-summary-cta'>
					<div className='tf-reviews-summary-cta-title'>Ai încercat produsul?</div>
					<p>
						{userService.userValue && data.myReviewExists
							? "Ai lăsat deja un review pentru acest produs."
							: userService.userValue && !data.hasPurchased
							? "Poți lăsa un review doar dacă ai cumpărat acest produs."
							: "Spune-ți părerea și ajută alți clienți."}
					</p>
					<button type='button' className='tf-reviews-add-btn' onClick={handleAddReviewClick} disabled={!!(userService.userValue && !canReview)}>
						{userService.userValue && data.myReviewExists
							? "Ai lăsat deja un review"
							: userService.userValue && !data.hasPurchased
							? "Trebuie să cumperi produsul"
							: "Adaugă un review"}
					</button>
				</div>
			</div>

			<div className='tf-reviews-toolbar'>
				<select value={sort} onChange={(e) => setSort(e.target.value)} className='tf-reviews-sort' aria-label='Sortează recenziile'>
					<option value='newest'>Cele mai noi</option>
					<option value='oldest'>Cele mai vechi</option>
					<option value='highest'>Nota cea mai mare</option>
					<option value='lowest'>Nota cea mai mică</option>
					<option value='mostLiked'>Cele mai apreciate</option>
				</select>
				{starFilter > 0 && (
					<button type='button' className='tf-reviews-clear-filter' onClick={() => setStarFilter(0)}>
						Șterge filtrul ({starFilter} stele) <X size={14} />
					</button>
				)}
			</div>

			<div className='tf-reviews-list'>
				{reviews.length === 0 && <div className='tf-reviews-empty'>Momentan nu există recenzii pentru acest produs.</div>}

				{reviews.map((review) => {
					const canManageReview = isAdmin || review.isMine;
					return (
						<div className={`tf-review-card ${review.hidden ? "is-hidden" : ""}`} key={review.id}>
							{canManageReview && (
								<div className='tf-review-card-admin-actions'>
									{isAdmin && review.hidden && <span className='tf-review-flag-badge'>ascuns</span>}
									<button type='button' onClick={() => handleEditReview(review)} aria-label='Editează'>
										<Pencil size={16} />
									</button>
									{isAdmin && (
										<button type='button' onClick={() => handleHideReview(review)} aria-label={review.hidden ? "Arată" : "Ascunde"}>
											{review.hidden ? <Eye size={16} /> : <EyeOff size={16} />}
										</button>
									)}
									<button type='button' onClick={() => handleDeleteReview(review)} aria-label='Șterge'>
										<Trash2 size={16} />
									</button>
								</div>
							)}

							<div className='tf-review-card-head'>
								<Avatar url={review.userAvatar} name={review.userName} size={36} />
								<div className='tf-review-card-head-info'>
									<StarRow rating={review.rating} />
									{review.verifiedPurchase && (
										<span className='tf-review-verified-badge'>
											<BadgeCheck size={14} /> Cumpărător verificat
										</span>
									)}
								</div>
							</div>

							{review.title && <h3 className='tf-review-card-title'>{review.title}</h3>}
							<p className='tf-review-card-text'>{review.text}</p>

							 {Array.isArray(review.images) && review.images.length > 0 && (
								<div className='tf-review-card-images'>
									{review.images.map((url, i) => (
										<button type='button' key={url} className='tf-review-card-image' onClick={() => setLightbox({ images: review.images, index: i })}>
											<img src={url} alt='' />
										</button>
									))}
								</div>
							)}

							<div className='tf-review-card-meta'>
								<span className='tf-review-card-author'>{review.userName}</span>
								<span className='tf-review-card-date'>{formatDate(review.createdAt)}</span>
								{review.variantLabel && <span className='tf-review-card-variant'>Variantă: {review.variantLabel}</span>}
							</div>

							<div className='tf-review-card-actions'>
								<button type='button' className={`tf-review-like-btn ${review.likedByMe ? "liked" : ""}`} onClick={() => handleLike(review.id)}>
									<ThumbsUp size={16} fill={review.likedByMe ? "#3b82f6" : "none"} /> {review.likesCount}
								</button>
								<button
									type='button'
									className='tf-review-comment-btn'
									onClick={() => setOpenComments((prev) => ({ ...prev, [review.id]: !prev[review.id] }))}>
									<MessageCircle size={16} /> {review.commentsCount} comentarii
								</button>
							</div>

							{openComments[review.id] && (
								<div className='tf-review-comments-block'>
									{review.comments.map((comment) => (
										<CommentItem
											key={comment.id}
											comment={{ ...comment, reviewID: review.id }}
											reviewID={review.id}
											depth={0}
											isAdmin={isAdmin}
											replyingTo={replyingTo[review.id]}
											setReplyingTo={(id) => setReplyingTo((prev) => ({ ...prev, [review.id]: id }))}
											onSubmitReply={handleSubmitComment}
											onEditComment={handleEditComment}
											onDeleteComment={handleDeleteComment}
											onHideComment={handleHideComment}
										/>
									))}

									<div className='tf-review-new-comment'>
										<textarea
											rows={2}
											placeholder='Adaugă un comentariu...'
											value={newComment[review.id] || ""}
											onChange={(e) => setNewComment((prev) => ({ ...prev, [review.id]: e.target.value }))}
										/>
										{isAdmin && (
											<label className='tf-review-admin-checkbox'>
												<input
													type='checkbox'
													checked={!!newCommentAsAdmin[review.id]}
													onChange={(e) => setNewCommentAsAdmin((prev) => ({ ...prev, [review.id]: e.target.checked }))}
												/>
												<ShieldCheck size={13} /> Comentează ca eMagyl Support
											</label>
										)}
										<button
											type='button'
											onClick={() => {
												const value = (newComment[review.id] || "").trim();
												if (!value) return;
												handleSubmitComment(review.id, null, value, !!newCommentAsAdmin[review.id]);
												setNewComment((prev) => ({ ...prev, [review.id]: "" }));
												setNewCommentAsAdmin((prev) => ({ ...prev, [review.id]: false }));
											}}>
											Trimite
										</button>
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>

			{totalPages > 1 && (
				<div className='tf-reviews-pagination'>
					<button type='button' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
						<ChevronLeft size={18} />
					</button>
					<span>
						Pagina {page} din {totalPages}
					</span>
					<button type='button' disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
						<ChevronRight size={18} />
					</button>
				</div>
			)}

			{showForm && (
				<ReviewForm
					mode={formMode}
					productID={product.id}
					initialData={editingReview}
					onClose={() => setShowForm(false)}
					onSuccess={fetchReviews}
				/>
			)}

			{lightbox && (
				<Lightbox
					images={lightbox.images}
					index={lightbox.index}
					onClose={() => setLightbox(null)}
					onNavigate={(i) => setLightbox((prev) => ({ ...prev, index: i }))}
				/>
			)}

			{confirmDialog && (
				<ConfirmDrawer
					title={confirmDialog.title}
					text={confirmDialog.text}
					confirmText={confirmDialog.confirmText}
					onConfirm={() => {
						dialogResolverRef.current?.(true);
						setConfirmDialog(null);
					}}
					onCancel={() => {
						dialogResolverRef.current?.(false);
						setConfirmDialog(null);
					}}
				/>
			)}

			{editDialog && (
				<EditTextDrawer
					title={editDialog.title}
					initialValue={editDialog.initialValue}
					onSave={(value) => {
						dialogResolverRef.current?.(value);
						setEditDialog(null);
					}}
					onCancel={() => {
						dialogResolverRef.current?.(null);
						setEditDialog(null);
					}}
				/>
			)}
		</div>
	);
}
